import React, { useState, useEffect } from "react";
import { Modal, Table, Badge, Button } from "react-bootstrap";
import { Switch } from "@mui/material";
import offerAPI from "../../services/offerapi";
import ConfirmationDialog from "../../components/modal/ConfirmationDialog";
import AlertDialog from "../../components/modal/AlertDialog";

const BranchesModal = ({
    branches,
    availableBranches,
    setAvailableBranches,
    branchesModalVisible,
    handleCloseBranchesModal,
    selectedOfferId
}) => {
    const [activeBranches, setActiveBranches] = useState([]);
    const [inactiveBranches, setInactiveBranches] = useState([]);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [alertMessage, setAlertMessage] = useState(""); // Manage alert message
    const [isAlertVisible, setIsAlertVisible] = useState(false); // Manage alert visibility

    // console.log(availableBranches, 'availableBranches');
    // Update active and inactive branches when availableBranches changes
    useEffect(() => {
        const active = [];
        const inactive = [];
        // availableBranches.forEach(branch => {
        //     if (branch.status === 1) {
        //         active.push(branch);
        //     } else {
        //         inactive.push(branch);
        //     }
        // });
        branches.forEach(branch => {
            const isBranchAvailable = availableBranches.some(availableBranch => availableBranch.branch_id === branch.branchId);
            if (isBranchAvailable) {
                active.push({ branch_id: branch.branchId, branchName: branch.branchName, status: 1 });
            } else {
                inactive.push({ branch_id: branch.branchId, branchName: branch.branchName, status: 1 });
            }
        });
        setActiveBranches(active);
        setInactiveBranches(inactive);
    }, [availableBranches, branches]);
    // console.log(activeBranches, 'activeBranches');
    // console.log(inactiveBranches, 'inactiveBranches');

    // Toggle branch status (activate/deactivate)
    const handleToggleBranchStatus = (branchId, currentStatus) => {
        const updatedActiveBranches = [...activeBranches];
        const updatedInactiveBranches = [...inactiveBranches];

        if (currentStatus === 1) {
            // Move from active to inactive
            const index = updatedActiveBranches.findIndex(
                (branch) => branch.branch_id === branchId
            );
            if (index !== -1) {
                const branch = updatedActiveBranches.splice(index, 1)[0];
                updatedInactiveBranches.push(branch);
            }
        } else {
            // Move from inactive to active
            // console.log(inactiveBranches, 'inactiveBranches');
            const index = updatedInactiveBranches.findIndex(
                (branch) => branch.branch_id === branchId
            );
            // console.log(index, 'index');

            if (index !== -1) {
                const branch = updatedInactiveBranches.splice(index, 1)[0];
                updatedActiveBranches.push(branch);
            }
        }

        setActiveBranches(updatedActiveBranches);
        setInactiveBranches(updatedInactiveBranches);

        updateBranchStatusInAPI(branchId, currentStatus === 1 ? 2 : 1);
    };

    const updateBranchStatusInAPI = async (branchIds, newStatus) => {
        try {
            let resp;
            if (Array.isArray(branchIds)) {
                resp = await offerAPI.updateOfferBranchStatus({
                    action: "updateBulk",
                    offer_id: selectedOfferId,
                    branch_id: branchIds,
                    status: newStatus,
                });
            } else {
                resp = await offerAPI.updateOfferBranchStatus({
                    action: "update",
                    offer_id: selectedOfferId,
                    branch_id: branchIds,
                    status: newStatus,
                });
            }

            if (resp && resp.message) {
                setAlertMessage(resp.message);
                setIsAlertVisible(true);
            } else {
                setAlertMessage("Process failed, kindly try again later!");
                setIsAlertVisible(true);
            }
        } catch (error) {
            // console.error("Error updating branch status:", error);
            setAlertMessage("Error updating branch status. Please try again.");
            setIsAlertVisible(true);
        }
    };

    // Handle bulk actions (activate or deactivate all branches)
    const handleBulkAction = (action) => {
        setConfirmationAction(action);
        setIsConfirmationVisible(true);
    };

    // Confirm bulk action (activate or deactivate all)
    const confirmBulkAction = () => {
        const updatedActiveBranches = [...activeBranches];
        const updatedInactiveBranches = [...inactiveBranches];
        let branchesToUpdate = [];

        if (confirmationAction === "activateAll") {
            branchesToUpdate = updatedInactiveBranches.map(branch => branch.branch_id);
            updatedActiveBranches.push(...updatedInactiveBranches);
            updatedInactiveBranches.length = 0;
        } else if (confirmationAction === "deactivateAll") {
            branchesToUpdate = updatedActiveBranches.map(branch => branch.branch_id);
            updatedInactiveBranches.push(...updatedActiveBranches);
            updatedActiveBranches.length = 0;
        }

        setActiveBranches(updatedActiveBranches);
        setInactiveBranches(updatedInactiveBranches);

        // Make API call for bulk update
        updateBranchStatusInAPI(branchesToUpdate, confirmationAction === "activateAll" ? 1 : 2);
        setIsConfirmationVisible(false); // Close confirmation dialog
    };

    const cancelBulkAction = () => {
        setIsConfirmationVisible(false); // Close confirmation dialog without action
    };

    const isBranchActive = (branchId) => {
        return activeBranches.some((entry) => entry.branch_id === branchId);
    };

    return (
        <Modal show={branchesModalVisible} onHide={handleCloseBranchesModal} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Offer Branches</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex gap-2 mb-2">
                    <Button
                        variant="primary"
                        onClick={() => handleBulkAction("activateAll")}
                        className="mb-3 btn btn-sm"
                        size="sm"
                    >
                        Activate All
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => handleBulkAction("deactivateAll")}
                        className="mb-3 ml-2 btn-sm"
                    >
                        Deactivate All
                    </Button>
                </div>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Branch Name</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {branches.map((branch) => {
                            const isActive = isBranchActive(branch.branchId);

                            return (
                                <tr key={branch.branchId}>
                                    <td>{branch.branchName}</td>
                                    <td>
                                        <Badge pill bg={isActive ? "success" : "danger"}>
                                            {isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Switch
                                            checked={isActive}
                                            onChange={() =>
                                                handleToggleBranchStatus(
                                                    branch.branchId,
                                                    isActive ? 1 : 2
                                                )
                                            }
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseBranchesModal}>
                    Close
                </Button>
            </Modal.Footer>

            {isConfirmationVisible && (
                <ConfirmationDialog
                    message={`Are you sure you want to ${confirmationAction === "activateAll" ? "activate" : "deactivate"} all branches?`}
                    onConfirm={confirmBulkAction}
                    onCancel={cancelBulkAction}
                />
            )}

            {isAlertVisible && (
                <AlertDialog
                    message={alertMessage}
                    onClose={() => setIsAlertVisible(false)}
                />
            )}
        </Modal>
    );
};

export default BranchesModal;
